import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Repeat, Plus, Trash, PlayCircle, Pause, Play } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY_LINE = { account_code: "", debit: "", credit: "", description: "" };
const TYPES = [
  { v: "recurring", l: "Recurring (fixed monthly)" },
  { v: "accrual", l: "Accrual (auto-reverses next period)" },
  { v: "prepayment", l: "Prepayment amortisation" },
];

function TemplateDialog({ open, onOpenChange, accounts, onSaved }) {
  const [form, setForm] = useState({ name: "", template_type: "recurring", memo: "", day_of_month: 1, start_period: new Date().toISOString().slice(0, 7), end_period: "" });
  const [lines, setLines] = useState([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setForm({ name: "", template_type: "recurring", memo: "", day_of_month: 1, start_period: new Date().toISOString().slice(0, 7), end_period: "" }); setLines([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]); }
  }, [open]);
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const totals = useMemo(() => {
    const dr = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
    const cr = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
    return { dr: Math.round(dr * 100) / 100, cr: Math.round(cr * 100) / 100 };
  }, [lines]);
  const balanced = Math.abs(totals.dr - totals.cr) < 0.005 && totals.dr > 0;
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.post("/finance/gl/recurring", {
        ...form, end_period: form.end_period || null, day_of_month: parseInt(form.day_of_month) || 1,
        lines: lines.filter((l) => l.account_code).map((l) => ({
          account_code: l.account_code, debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0, description: l.description || "" })),
      });
      toast.success("Template created");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="recurring-dialog">
        <DialogHeader>
          <DialogTitle>New journal template</DialogTitle>
          <DialogDescription>Generated journals are created as DRAFTS — a second finance user posts them (maker-checker). Accruals auto-reverse on the 1st of the next period once posted.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Monthly office rent" data-testid="recurring-name" /></div>
          <div><Label>Type</Label>
            <Select value={form.template_type} onValueChange={(v) => setForm({ ...form, template_type: v })}>
              <SelectTrigger data-testid="recurring-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Day of month</Label><Input type="number" min="1" max="28" value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} /></div>
          <div><Label>Start period</Label><Input type="month" value={form.start_period} onChange={(e) => setForm({ ...form, start_period: e.target.value })} data-testid="recurring-start" /></div>
          <div><Label>End period (optional)</Label><Input type="month" value={form.end_period} onChange={(e) => setForm({ ...form, end_period: e.target.value })} /></div>
          <div className="col-span-3"><Label>Memo</Label><Input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} placeholder="Defaults to name" /></div>
        </div>
        <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_110px_110px_1fr_32px] items-center gap-2">
              <Select value={l.account_code || undefined} onValueChange={(v) => setLine(i, "account_code", v)}>
                <SelectTrigger className="h-9" data-testid={`recurring-line-account-${i}`}><SelectValue placeholder="Account…" /></SelectTrigger>
                <SelectContent>{accounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" min="0" step="0.01" placeholder="Debit" value={l.debit} onChange={(e) => setLine(i, "debit", e.target.value)} data-testid={`recurring-line-debit-${i}`} />
              <Input type="number" min="0" step="0.01" placeholder="Credit" value={l.credit} onChange={(e) => setLine(i, "credit", e.target.value)} data-testid={`recurring-line-credit-${i}`} />
              <Input placeholder="Description" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} />
              <Button variant="outline" size="sm" className="h-9 px-2" disabled={lines.length <= 2} onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))}><Trash size={12} /></Button>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, { ...EMPTY_LINE }])}><Plus size={12} className="mr-1" /> Add line</Button>
          <div className="text-sm font-mono-alt tabular-nums">
            DR <b>{formatCurrency(totals.dr)}</b> · CR <b>{formatCurrency(totals.cr)}</b> ·{" "}
            <span className={balanced ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold"}>{balanced ? "Balanced ✓" : "Unbalanced"}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="btn-primary-blue" onClick={save} disabled={busy || !balanced} data-testid="recurring-save-btn">{busy ? "Saving…" : "Create template"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RecurringJournalsPanel({ canEdit, accounts, onGenerated }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [genPeriod, setGenPeriod] = useState(new Date().toISOString().slice(0, 7));

  const load = async () => {
    try { const { data } = await api.get("/finance/gl/recurring"); setItems(data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const generate = async (id) => {
    try {
      const { data } = await api.post(`/finance/gl/recurring/${id}/generate`, { period: genPeriod });
      toast.success(`${data.entry_no} created as draft for ${genPeriod}`);
      load(); onGenerated?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const generateDue = async () => {
    try {
      const { data } = await api.post("/finance/gl/recurring/generate-due", { period: genPeriod });
      toast.success(`${data.generated.length} draft(s) generated, ${data.skipped.length} skipped`);
      load(); onGenerated?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = async (t) => {
    try { await api.patch(`/finance/gl/recurring/${t.id}`, { status: t.status === "active" ? "paused" : "active" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/finance/gl/recurring/${id}`); toast.success("Template deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="dc-card overflow-hidden" data-testid="recurring-panel">
      <div className="border-b border-slate-100 px-5 py-3 flex flex-wrap items-center gap-2">
        <Repeat size={18} weight="fill" style={{ color: "#1E5CBA" }} />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Recurring · Accrual · Prepayment Templates</h3>
        <div className="ml-auto flex items-center gap-2">
          <Input type="month" className="h-8 w-40" value={genPeriod} onChange={(e) => setGenPeriod(e.target.value)} data-testid="recurring-gen-period" />
          {canEdit && items.length > 0 && (
            <Button size="sm" variant="outline" className="h-8" onClick={generateDue} data-testid="recurring-generate-due">
              <PlayCircle size={13} className="mr-1" /> Generate all due
            </Button>
          )}
          {canEdit && (
            <Button size="sm" className="btn-primary-blue h-8" onClick={() => setOpen(true)} data-testid="recurring-add-btn">
              <Plus size={12} className="mr-1" /> Template
            </Button>
          )}
        </div>
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-5 text-sm text-slate-500" data-testid="recurring-empty">No templates yet — set up recurring rent, accrued utilities or prepayment amortisation to auto-draft monthly journals.</div>
      ) : (
        <div className="responsive-table-wrap">
          <table className="data-table w-full min-w-[900px]" data-testid="recurring-table">
            <thead><tr><th>Name</th><th>Type</th><th>Window</th><th className="text-right">Amount</th><th>Last generated</th><th>Status</th><th className="w-40">Actions</th></tr></thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} data-testid={`recurring-row-${t.id}`}>
                  <td className="px-5 py-3 text-sm font-semibold text-slate-900">{t.name}{t.auto_reverse && <span className="ml-1.5 rounded bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700">auto-rev</span>}</td>
                  <td className="text-xs uppercase tracking-wider text-slate-500">{t.template_type}</td>
                  <td className="font-mono-alt text-xs tabular-nums">{t.start_period} → {t.end_period || "∞"} · d{t.day_of_month}</td>
                  <td className="text-right font-mono-alt text-xs tabular-nums">{formatCurrency(t.total_debit)}</td>
                  <td className="font-mono-alt text-xs tabular-nums">{t.last_generated_period || "—"} ({t.generated_count || 0})</td>
                  <td><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${t.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{t.status.toUpperCase()}</span></td>
                  <td>
                    {canEdit && (
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" className="h-7" disabled={t.status !== "active"} onClick={() => generate(t.id)} data-testid={`recurring-generate-${t.id}`}>
                          <PlayCircle size={12} className="mr-1" /> Generate
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => toggle(t)} title={t.status === "active" ? "Pause" : "Resume"} data-testid={`recurring-toggle-${t.id}`}>
                          {t.status === "active" ? <Pause size={12} /> : <Play size={12} />}
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => remove(t.id)} data-testid={`recurring-delete-${t.id}`}><Trash size={12} /></Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <TemplateDialog open={open} onOpenChange={setOpen} accounts={accounts} onSaved={load} />
    </div>
  );
}
